import React from 'react'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Checkbox } from 'primereact/checkbox'
import { InputTextarea } from 'primereact/inputtextarea'
import { Calendar } from 'primereact/calendar'
import { Toast } from 'primereact/toast'
import { classNames } from 'primereact/utils'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { createObituary, getObituary, updateObituary } from '../../api/obituaries'
import { useNavigate, useParams } from 'react-router-dom'
import ClassicAppBar from '../../components/ClassicAppBar'
import BreadcrumbsNav from '../../components/BreadcrumbsNav'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../store/store'
import { setLanguage } from '../../store/slices/languageSlice'
import { toggleMode } from '../../store/slices/themeSlice'
import { useTranslation } from 'react-i18next'
import { obituarySchema as schema, type FormValues, type FormInput } from '../../lib/validation'

const ObituaryForm: React.FC = () => {
  const { id } = useParams()
  const isEdit = !!id
  const navigate = useNavigate()
  const toast = React.useRef<Toast>(null)
  const { i18n } = useTranslation()
  const dispatch = useDispatch()
  const themeMode = useSelector((state: RootState) => state.theme?.mode || 'light')

  const { control, handleSubmit, reset, formState: { isSubmitting } } = useForm<FormInput, any, FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      published: false,
      dateOfDeath: null
    }
  })

  const handleLanguageChange = (lang: 'en' | 'ta' | 'de') => {
    i18n.changeLanguage(lang)
    dispatch(setLanguage(lang))
  }

  const handleToggleMode = () => {
    dispatch(toggleMode())
  }

  React.useEffect(() => {
    if (isEdit) {
      getObituary(Number(id)).then(d => {
        if (d) {
          reset({
            ...d,
            dateOfDeath: d.dateOfDeath ? new Date(d.dateOfDeath) : null
          })
        }
      })
    }
  }, [id, isEdit, reset])

  const onSubmit = async (v: FormValues) => {
    try {
      // Format date to YYYY-MM-DD for API if needed, or keep as Date object if API handles it
      const formattedValues = {
        ...v,
        dateOfDeath: v.dateOfDeath ? v.dateOfDeath.toISOString().split('T')[0] : ''
      }

      if (isEdit) await updateObituary(Number(id), formattedValues)
      else await createObituary(formattedValues)

      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Obituary saved successfully', life: 3000 })
      setTimeout(() => navigate('/admin/obituaries'), 1000)
    } catch (e: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e.message || 'An error occurred', life: 3000 })
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-column">
      <ClassicAppBar
        mode={themeMode}
        onToggleMode={handleToggleMode}
        onChangeLang={handleLanguageChange}
      />
      <div className="container mx-auto p-4 lg:p-6 max-w-4xl">
        <BreadcrumbsNav items={[{ label: 'Admin', to: '/admin' }, { label: 'Obituaries', to: '/admin/obituaries' }, { label: isEdit ? 'Edit' : 'New' }]} />

        <div className="surface-card p-4 shadow-2 border-round w-full lg:w-8 mx-auto mt-4">
          <div className="text-3xl font-medium text-900 mb-4">{isEdit ? 'Edit Obituary' : 'New Obituary'}</div>
          <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
            <div className="field mb-4">
              <label htmlFor="name" className="font-medium text-900">Name</label>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <InputText id="name" {...field} className={classNames({ 'p-invalid': fieldState.error })} />
                    {fieldState.error && <small className="p-error">{fieldState.error.message}</small>}
                  </>
                )}
              />
            </div>

            <div className="formgrid grid">
              <div className="field col-12 md:col-4 mb-4">
                <label htmlFor="age" className="font-medium text-900">Age</label>
                <Controller
                  name="age"
                  control={control}
                  render={({ field }) => (
                    <InputText id="age" type="number" {...field} value={field.value?.toString() || ''} onChange={(e) => field.onChange(parseInt(e.target.value) || '')} />
                  )}
                />
              </div>
              <div className="field col-12 md:col-8 mb-4">
                <label htmlFor="residence" className="font-medium text-900">Residence</label>
                <Controller
                  name="residence"
                  control={control}
                  render={({ field }) => (
                    <InputText id="residence" {...field} value={field.value || ''} />
                  )}
                />
              </div>
            </div>

            <div className="field mb-4">
              <label htmlFor="origin" className="font-medium text-900">Origin</label>
              <Controller
                name="origin"
                control={control}
                render={({ field }) => (
                  <InputText id="origin" {...field} value={field.value || ''} />
                )}
              />
            </div>

            <div className="field mb-4">
              <label htmlFor="dateOfDeath" className="font-medium text-900">Date of Death</label>
              <Controller
                name="dateOfDeath"
                control={control}
                render={({ field }) => (
                  <Calendar id="dateOfDeath" value={field.value} onChange={field.onChange} dateFormat="yy-mm-dd" showIcon />
                )}
              />
            </div>

            <div className="field mb-4">
              <label htmlFor="photoUrl" className="font-medium text-900">Photo URL</label>
              <Controller
                name="photoUrl"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <InputText id="photoUrl" {...field} value={field.value || ''} className={classNames({ 'p-invalid': fieldState.error })} />
                    {fieldState.error && <small className="p-error">{fieldState.error.message}</small>}
                  </>
                )}
              />
            </div>

            <div className="field mb-4">
              <label htmlFor="content" className="font-medium text-900">Content</label>
              <Controller
                name="content"
                control={control}
                render={({ field }) => (
                  <InputTextarea id="content" {...field} value={field.value || ''} rows={6} autoResize />
                )}
              />
            </div>

            <div className="field-checkbox mb-4">
              <Controller
                name="published"
                control={control}
                render={({ field }) => (
                  <Checkbox inputId="published" onChange={(e) => field.onChange(e.checked)} checked={field.value || false} />
                )}
              />
              <label htmlFor="published" className="font-medium text-900 ml-2">Published</label>
            </div>

            <div className="flex justify-content-end gap-2">
              <Button label="Cancel" icon="pi pi-times" type="button" onClick={() => navigate(-1)} className="p-button-text text-orange-600" />
              <Button label="Save" icon="pi pi-check" type="submit" loading={isSubmitting} className="bg-orange-600 border-orange-600" />
            </div>
          </form>
        </div>
      </div>
      <Toast ref={toast} />
    </div>
  )
}

export default ObituaryForm
